// Interface du raid : réticule, santé, arme, minuteur, extractions, indicateurs de son et de dégâts, écran de fin.
import { AMMO_OF, ITEMS } from '../shared/items';
import { countOf } from '../shared/inventory';
import { iconFor, iconOf } from './icons';
import { MAX_LEVEL, xpFor } from '../shared/hideout';
import type { Loadout, ServerMsg, Snapshot } from '../shared/protocol';
import { WEAPONS } from '../shared/weapons';

const $ = (id: string) => document.getElementById(id)!;
const esc = (s: string) => s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
const mmss = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

interface FrameState {
  mouseX: number; mouseY: number; aiming: boolean; alive: boolean; onTarget: boolean;
  nextImpact: { x: number; y: number } | null; player: { x: number; y: number }; stance: string; prompt: string;
  extracts: { name: string; dist: number; pos: { x: number; y: number } }[];
  conts: { key: string; label: string; pos: { x: number; y: number }; state: 'near' | 'far' | 'done' | 'dead' }[];
}

const PX_PER_DEG = 3.2;
const ICON_HELMET = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3 15a9 8 0 0 1 18 0v2H3z"/><path d="M2 17h20v2H2z"/></svg>';
const ICON_VEST = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 3h3l2 2 2-2h3l3 4v13H4V7z"/></svg>';
// Soins rangés dans la barre d'objets (touches 3, 4, 5) : un type par case, dans un ordre fixe
const MED_ORDER = ['m_bandage', 'm_kit', 'm_splint'];
export function hotbarMeds(l: Loadout | null): ({ id: string; n: number } | null)[] {
  return MED_ORDER.map(id => { const n = l ? countOf(l.bag, id) + countOf(l.pouch, id) : 0; return n ? { id, n } : null; });
}

export class Hud {
  private kickV = 0;
  private hitUntil = 0;
  private killUntil = 0;
  private hurtUntil = 0;
  private cues: { el: HTMLElement; angle: number; at: number; life: number }[] = [];
  private tags = new Map<number, HTMLElement>();
  private extEls: HTMLElement[] = [];
  private contEls = new Map<string, HTMLElement>();
  private you: Snapshot['you'] | null = null;

  // Écran de chargement : progression (0 à 1) et étape en cours
  loading(f: number, step?: string) {
    $('menu').classList.add('hidden');
    $('loading').classList.remove('hidden');
    ($('loadBar') as HTMLElement).style.width = `${Math.round(f * 100)}%`;
    if (step) $('loadStep').textContent = step;
  }

  showGame(code: string) {
    $('loading').classList.add('hidden');
    $('endScreen').classList.add('hidden');
    $('hud').classList.remove('hidden');
    $('roomCode').textContent = code;
    document.body.classList.add('in-game');
  }

  disconnected() { $('disc').classList.remove('hidden'); document.body.classList.remove('in-game'); }

  toast(msg: string) {
    const t = document.createElement('div');
    t.className = 'toast';
    t.textContent = msg;
    $('toasts').append(t);
    while ($('toasts').children.length > 4) $('toasts').firstChild!.remove();
    setTimeout(() => t.remove(), 3200);
  }

  // Boussole : cap de la caméra (0 = nord, sens horaire), une graduation tous les 15°
  private compassBuilt = false;
  compass(headingDeg: number) {
    const strip = $('compassStrip');
    if (!this.compassBuilt) {
      const NAMES: Record<number, string> = { 0: 'N', 45: 'NE', 90: 'E', 135: 'SE', 180: 'S', 225: 'SO', 270: 'O', 315: 'NO' };
      let h = '';
      for (let d = -360; d <= 720; d += 15) { const a = ((d % 360) + 360) % 360; h += `<span class="${NAMES[a] ? 'card' : ''}" style="left:${(d + 360) * PX_PER_DEG}px">${NAMES[a] ?? a}</span>`; }
      strip.innerHTML = h;
      this.compassBuilt = true;
    }
    const hd = ((headingDeg % 360) + 360) % 360;
    strip.style.transform = `translateX(${-(hd + 360) * PX_PER_DEG}px)`;
  }

  vitals(you: Snapshot['you'], mag: number, ping: number, loadout: Loadout | null, slot: 1 | 2) {
    this.you = you;
    ($('hpFill') as HTMLElement).style.width = `${you.hp}%`;
    $('hpFill').classList.toggle('low', you.hp <= 30);
    $('hpValue').textContent = String(you.hp);
    const w = you.w ? WEAPONS[you.w] : null;
    $('wName').textContent = w ? w.name : 'Sans arme';
    $('wMode').textContent = w ? (w.auto ? 'Auto' : 'Semi') : '';
    $('wMag').textContent = w ? String(mag) : '—';
    // Réserve : toutes les cartouches du bon calibre dans le sac et la poche
    const reserve = w && loadout ? AMMO_OF[w.caliber].reduce((s, id) => s + countOf(loadout.bag, id) + countOf(loadout.pouch, id), 0) : 0;
    $('wMax').textContent = w ? `/ ${reserve}` : '';
    const held = slot === 1 ? loadout?.primary : loadout?.secondary;
    const ic = held ? iconFor(held) : '';
    const wi = $('wIcon') as HTMLImageElement;
    if (wi.dataset.src !== ic) { wi.dataset.src = ic; if (ic) wi.src = ic; wi.classList.toggle('hidden', !ic); }
    $('wMag').classList.toggle('empty', !!w && mag === 0);
    const r = $('wReload');
    r.classList.toggle('hidden', !you.reload || !w);
    if (you.reload && w) (r.firstElementChild as HTMLElement).style.width = `${100 - (you.reload / w.reload) * 100}%`;
    const name = (s: 1 | 2) => { const it = s === 1 ? loadout?.primary : loadout?.secondary; return it ? ITEMS[it.id]!.short : '—'; };
    $('slots').innerHTML = ([1, 2] as const).map(s => `<span class="${s === slot ? 'on' : ''}"><b>${s}</b>${esc(name(s))}</span>`).join('');
    $('ping').textContent = `${ping} ms`;
    $('timer').textContent = mmss(you.left);
    // Protections et blessures
    const bars = ([[ICON_HELMET, 'Casque', you.armor[0], you.armor[1]], [ICON_VEST, 'Gilet', you.armor[2], you.armor[3]]] as const).filter(b => b[3] > 0)
      .map(([ic, n, d, m]) => `<div class="vrow ab" title="${n}"><span class="vi">${ic}</span><i><b style="width:${(d / m) * 100}%"></b></i><small>${Math.round(d)}</small></div>`).join('');
    // Barre d'objets : 1 arme principale, 2 pistolet, 3 à 5 soins du sac (touche pour les utiliser)
    const cells = [loadout?.primary ?? null, loadout?.secondary ?? null].map((it, i) => it ? `<div class="hb${slot === i + 1 ? ' on' : ''}"><small>${i + 1}</small><img src="${iconFor(it)}" alt=""></div>` : `<div class="hb"><small>${i + 1}</small></div>`)
      .concat(hotbarMeds(loadout).map((m, i) => m ? `<div class="hb"><small>${i + 3}</small><img src="${iconOf(m.id)}" alt=""><b>${m.n}</b></div>` : `<div class="hb"><small>${i + 3}</small></div>`)).join('');
    if ($('hotbar').dataset.h !== cells) { $('hotbar').innerHTML = cells; $('hotbar').dataset.h = cells; }
    if ($('armorBars').dataset.h !== bars) { $('armorBars').innerHTML = bars; $('armorBars').dataset.h = bars; }
    const st = (you.status & 1 ? '<span class="bad">Saignement · V</span>' : '') + (you.status & 2 ? '<span class="bad">Fracture · V</span>' : '');
    if ($('status').dataset.h !== st) { $('status').innerHTML = st; $('status').dataset.h = st; }
    $('timer').classList.toggle('late', you.left < 180);
    // Soin et extraction en cours
    const act = $('action');
    if (you.heal > 0) { act.classList.remove('hidden'); $('actionText').textContent = 'Soin en cours'; ($('actionBar') as HTMLElement).style.width = `${you.heal * 100}%`; }
    else if (you.ext > 0) { act.classList.remove('hidden'); $('actionText').textContent = `Extraction : ${you.extName}`; ($('actionBar') as HTMLElement).style.width = `${you.ext * 100}%`; }
    else act.classList.add('hidden');
  }

  quests(list: { title: string; have: number; n: number }[]) {
    // Objectifs : les contrats d'élimination en cours, puis l'extraction (toujours)
    const lines = [...list.map(q => ({ t: `${q.title} (${q.have}/${q.n})`, ok: q.have >= q.n })), { t: 'Rejoindre une extraction', ok: false }];
    const html = lines.map(l => `<div class="${l.ok ? 'ok' : ''}"><i></i><span>${esc(l.t)}</span></div>`).join('');
    if ($('questTrack').dataset.h !== html) { $('questTrack').innerHTML = html; $('questTrack').dataset.h = html; }
  }

  killfeed(killer: string, victim: string, weapon: string, mine: boolean, me: boolean) {
    const d = document.createElement('div');
    d.className = `kf ${mine ? 'mine' : ''} ${me ? 'me' : ''}`;
    d.innerHTML = `<b>${esc(killer)}</b><span>${esc(weapon)}</span><b>${esc(victim)}</b>`;
    $('killfeed').prepend(d);
    while ($('killfeed').children.length > 5) $('killfeed').lastChild!.remove();
    setTimeout(() => d.classList.add('fade'), 6000);
    setTimeout(() => d.remove(), 7000);
  }

  hitmark(kill: boolean, armor = false) {
    const t = performance.now();
    if (kill) this.killUntil = t + 450; else this.hitUntil = t + 160;
    $('crosshair').classList.toggle('armor', armor && !kill);
  }

  hurt(angle: number) {
    this.hurtUntil = performance.now() + 500;
    this.addCue('hurtArc', angle, 900);
  }

  soundCue(angle: number, kind: 'shot' | 'step') {
    // Un seul indicateur par direction proche : on le rafraîchit plutôt que d'empiler
    const same = this.cues.find(c => c.el.classList.contains(kind) && Math.abs(c.angle - angle) < 0.3);
    if (same) { same.at = performance.now(); same.angle = angle; return; }
    this.addCue(`cue ${kind}`, angle, 1300);
  }
  private addCue(cls: string, angle: number, life: number) {
    const el = document.createElement('div');
    el.className = cls;
    $('cues').append(el);
    this.cues.push({ el, angle, at: performance.now(), life });
  }

  kick(v: number) { this.kickV = Math.min(1.6, this.kickV + v); }

  nameTags(list: { id: number; name: string; hp: number; pos: { x: number; y: number } }[]) {
    const live = new Set<number>();
    for (const p of list) {
      live.add(p.id);
      let el = this.tags.get(p.id);
      if (!el) { el = document.createElement('div'); el.className = 'tag'; $('tags').append(el); this.tags.set(p.id, el); }
      const html = `<span>${esc(p.name)}</span><i style="width:${p.hp}%"></i>`;
      if (el.dataset.h !== html) { el.innerHTML = html; el.dataset.h = html; }
      el.style.transform = `translate(${p.pos.x}px, ${p.pos.y - 78}px) translate(-50%, -100%)`;
    }
    for (const [id, el] of this.tags) if (!live.has(id)) { el.remove(); this.tags.delete(id); }
  }

  frame(now: number, s: FrameState) {
    // Réticule : écartement selon le recul récent, plus serré en visée épaulée
    this.kickV = Math.max(0, this.kickV - 0.06);
    const ch = $('crosshair');
    ch.style.transform = `translate(${s.mouseX}px, ${s.mouseY}px)`;
    ch.style.setProperty('--gap', `${(s.aiming ? 5 : 9) + this.kickV * 9}px`);
    ch.classList.toggle('hit', now < this.hitUntil);
    ch.classList.toggle('kill', now < this.killUntil);
    ch.classList.toggle('target', s.onTarget);
    const ni = $('nextImpact');
    ni.classList.toggle('hidden', !s.nextImpact);
    if (s.nextImpact) ni.style.transform = `translate(${s.nextImpact.x}px, ${s.nextImpact.y}px)`;
    $('stance').textContent = s.stance;
    $('prompt').classList.toggle('hidden', !s.prompt);
    $('prompt').textContent = s.prompt;

    for (let i = this.cues.length - 1; i >= 0; i--) {
      const c = this.cues[i]!;
      const age = (now - c.at) / c.life;
      if (age >= 1) { c.el.remove(); this.cues.splice(i, 1); continue; }
      c.el.style.opacity = String(1 - age * age);
      c.el.style.transform = `translate(${s.player.x}px, ${s.player.y - 40}px) rotate(${c.angle}rad) translateY(-96px)`;
    }
    $('vignette').style.opacity = now < this.hurtUntil ? '1' : this.you && this.you.hp < 30 ? '0.55' : '0';

    // Meubles à fouiller visibles autour de soi
    const live = new Set<string>();
    for (const c of s.conts) {
      live.add(c.key);
      let el = this.contEls.get(c.key);
      if (!el) { el = document.createElement('div'); $('contMarks').append(el); this.contEls.set(c.key, el); }
      el.className = `cm ${c.state}`;
      const html = c.state === 'near' ? `<b>E</b>${esc(c.label)}` : esc(c.label);
      if (el.dataset.h !== html) { el.innerHTML = html; el.dataset.h = html; }
      el.style.transform = `translate(${c.pos.x}px, ${c.pos.y}px) translate(-50%, -100%)`;
    }
    for (const [k, el] of this.contEls) if (!live.has(k)) { el.remove(); this.contEls.delete(k); }

    // Extractions : un petit losange discret là où elles sont dans le décor (rien hors champ) ;
    // le nom n'apparaît que quand on regarde vers elle (près du réticule) ou qu'on en est tout près
    while (this.extEls.length < s.extracts.length) { const el = document.createElement('div'); el.className = 'ext'; $('extMarks').append(el); this.extEls.push(el); }
    const cx = innerWidth / 2, cy = innerHeight / 2;
    s.extracts.forEach((e, i) => {
      const el = this.extEls[i]!;
      const on = e.pos.x > 20 && e.pos.x < innerWidth - 20 && e.pos.y > 60 && e.pos.y < innerHeight - 150;
      el.classList.toggle('hidden', !on);
      if (!on) return;
      const near = Math.hypot(e.pos.x - cx, e.pos.y - cy) < 150 || e.dist < 25;
      el.classList.toggle('near', near);
      const html = `<i></i><b>${esc(e.name)}</b><span>${Math.round(e.dist)} m</span>`;
      if (el.innerHTML !== html) el.innerHTML = html;
      el.style.opacity = near ? '1' : String(Math.max(0.35, 0.8 - e.dist / 500));
      el.style.transform = `translate(${e.pos.x.toFixed(0)}px, ${e.pos.y.toFixed(0)}px) translate(-50%, -100%)`;
    });
  }

  endScreen(m: Extract<ServerMsg, { t: 'end' }>, back: () => void) {
    $('hud').classList.add('hidden');
    document.body.classList.remove('in-game');
    const ok = m.result === 'extracted';
    $('endKicker').textContent = ok ? 'Raid réussi' : 'Raid perdu';
    $('endKicker').className = `endKicker ${ok ? '' : 'bad'}`;
    $('endTitle').textContent = { extracted: 'Extrait', dead: 'Mort en raid', mia: 'Porté disparu' }[m.result];
    $('endMark').className = `endMark ${ok ? 'ok' : 'bad'}`;
    $('endLootLabel').textContent = ok ? 'Butin rapporté' : 'Poche sécurisée conservée';
    $('endLoot').textContent = `${m.value.toLocaleString('fr-FR')} cr`;
    $('endTime').textContent = mmss(m.duration);
    $('endKills').textContent = String(m.kills);
    $('endInfected').textContent = String(m.infected ?? 0); // ancien serveur : champ absent
    $('endKillerRow').classList.toggle('hidden', m.result !== 'dead');
    $('endKiller').textContent = m.result === 'dead' ? m.killer : '';
    // Combat : précision et dégâts (ancien serveur : bilan absent, ligne cachée)
    const f = m.fight;
    $('endFight').classList.toggle('hidden', !f || (!f.shots && !f.taken));
    if (f) $('endFightText').textContent = `${f.shots ? Math.min(100, Math.round((100 * f.hits) / f.shots)) : 0} % de précision · ${f.dealt} dégâts infligés · ${f.taken} reçus` // plombs du fusil à pompe : précision plafonnée;
    $('endXp').textContent = `+${m.xp.toLocaleString('fr-FR')} XP`;
    $('endLevel').textContent = String(m.level);
    $('endLevelUp').classList.toggle('hidden', !m.levelUp);
    // Progression vers le niveau suivant
    const lo = xpFor(m.level), hi = xpFor(m.level + 1), max = m.level >= MAX_LEVEL, total = m.totalXp ?? lo;
    ($('endBar') as HTMLElement).style.width = `${max ? 100 : Math.min(100, ((total - lo) / (hi - lo)) * 100)}%`;
    $('endXpText').textContent = max ? 'Niveau maximal' : `${(total - lo).toLocaleString('fr-FR')} / ${(hi - lo).toLocaleString('fr-FR')} XP`;
    $('endScreen').classList.remove('hidden');
    $('endBack').onclick = () => { $('endScreen').classList.add('hidden'); back(); };
  }

}
